/**
 * TimeWindowSelect — combined time-of-day + date-range chip selector.
 *
 * V3 Page 01 (2026-04-22) — absorbs the V2.5-B morning/afternoon/evening
 * chip row and adds a date-range mode for MM portal DNA parity
 * (MM-PORTAL-DNA-MIRROR §4 temporal segregation). When the
 * DASHBOARD_TIME_WINDOW flag is off the Dashboard renders the same V2.5-B
 * chips inline and never mounts this component — behaviour is byte-
 * identical with the pre-fork V2.6 dashboard.
 *
 * Date-range mode is optional: the parent supplies bounds + the full
 * event window. Omitting the bounds hides the range controls so the
 * component degrades cleanly on events that have only a single day.
 */

export type HourWindow = 'all' | 'morning' | 'afternoon' | 'evening'

export interface DateRangeBounds {
  /** ISO date (YYYY-MM-DD). Inclusive. */
  min: string
  /** ISO date. Inclusive. */
  max: string
}

export interface TimeWindowSelectProps {
  hourWindow: HourWindow
  onHourWindowChange: (next: HourWindow) => void
  /** Full event date bounds — if provided, a date-range pair is rendered. */
  dateBounds?: DateRangeBounds | null
  /** Selected range (defaults to full bounds). */
  dateRange?: { start: string; end: string } | null
  onDateRangeChange?: (next: { start: string; end: string } | null) => void
  'data-testid'?: string
}

const HOUR_OPTIONS: Array<{ key: HourWindow; label: string }> = [
  { key: 'all', label: 'All day' },
  { key: 'morning', label: 'Morning' },
  { key: 'afternoon', label: 'Afternoon' },
  { key: 'evening', label: 'Evening' },
]

export function TimeWindowSelect({
  hourWindow,
  onHourWindowChange,
  dateBounds,
  dateRange,
  onDateRangeChange,
  ...rest
}: TimeWindowSelectProps) {
  const testId = rest['data-testid'] || 'time-window-select'
  const start = dateRange?.start ?? dateBounds?.min ?? ''
  const end = dateRange?.end ?? dateBounds?.max ?? ''

  return (
    <div data-testid={testId} className="flex flex-wrap items-center gap-2">
      {HOUR_OPTIONS.map(({ key, label }) => {
        const active = hourWindow === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onHourWindowChange(key)}
            aria-pressed={active}
            data-testid={`${testId}-${key}`}
            className={
              active
                ? 'rounded-full border border-butler-400 bg-butler-100 px-2.5 py-1 text-butler-800 dark:border-butler-500 dark:bg-butler-900/60 dark:text-butler-100'
                : 'rounded-full border border-hairline bg-white px-2.5 py-1 text-ink-soft hover:border-butler-300 hover:text-butler-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-butler-200'
            }
          >
            {label}
          </button>
        )
      })}
      {dateBounds && onDateRangeChange && (
        <span
          data-testid={`${testId}-range`}
          className="ml-2 inline-flex items-center gap-1 rounded-full border border-hairline bg-white px-2.5 py-1 text-[10px] uppercase text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          <label className="sr-only" htmlFor={`${testId}-start`}>Start date</label>
          <input
            id={`${testId}-start`}
            data-testid={`${testId}-start`}
            type="date"
            value={start}
            min={dateBounds.min}
            max={dateBounds.max}
            onChange={(e) => onDateRangeChange({ start: e.target.value, end: end || dateBounds.max })}
            className="bg-transparent text-[11px] text-ink focus:outline-none dark:text-slate-200"
          />
          <span aria-hidden="true">→</span>
          <label className="sr-only" htmlFor={`${testId}-end`}>End date</label>
          <input
            id={`${testId}-end`}
            data-testid={`${testId}-end`}
            type="date"
            value={end}
            min={dateBounds.min}
            max={dateBounds.max}
            onChange={(e) => onDateRangeChange({ start: start || dateBounds.min, end: e.target.value })}
            className="bg-transparent text-[11px] text-ink focus:outline-none dark:text-slate-200"
          />
        </span>
      )}
    </div>
  )
}
